import React, { useState, useEffect, useMemo } from 'react';
import { useStore, put, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { toast } from '../utils/toast';
import { Edit2, Phone, MapPin, Heart, Droplets, User, Shield, Plane, CheckCircle, Clock, Cake, Loader2 } from 'lucide-react';

const AVATAR_COLORS = [
  '#f97316','#8b5cf6','#06b6d4','#10b981','#ef4444','#f59e0b','#3b82f6','#ec4899',
];

function getAvatarColor(name = '') {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function getInitials(name = '') {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?';
}

const FIELD_STYLE = {
  width: '100%', background: '#f9f9fb', border: '1px solid #e4e4e7',
  borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

const LABEL_STYLE = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: '#71717a', display: 'block', marginBottom: 5,
};

const SECTION_LABEL = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.1em', color: '#a1a1aa', marginBottom: 14,
};

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid #f4f4f5' }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={15} style={{ color: '#71717a' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, color: value ? '#09090b' : '#d4d4d8', fontStyle: value ? 'normal' : 'italic' }}>{value || 'Not set'}</div>
      </div>
    </div>
  );
}

export default function Profile({ currentUser }) {
  const { data: attendees, loading } = useStore('attendees');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const record = useMemo(
    () => attendees.find(a => (a.email || '').toLowerCase() === (currentUser.email || '').toLowerCase()) || null,
    [attendees, currentUser.email]
  );

  // Populate the edit form when the record arrives/changes
  useEffect(() => {
    const found = record;
    if (found) {
      setForm({
        phone: found.phone || '',
        address: found.address || '',
        originCity: found.originCity || '',
        birthday: found.birthday || '',
        dietary: found.dietary || 'Not specified',
        bloodGroup: found.bloodGroup || 'Unknown',
        tshirtSize: found.tshirtSize || 'M',
        roomNeeded: found.roomNeeded || false,
        emergencyContactName: found.emergencyContactName || '',
        emergencyContactPhone: found.emergencyContactPhone || '',
        emergencyContactRelation: found.emergencyContactRelation || 'Parent',
      });
    } else {
      setForm({
        phone: '', address: '', originCity: '', birthday: '',
        dietary: 'Not specified', bloodGroup: 'Unknown',
        tshirtSize: 'M', roomNeeded: false,
        emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: 'Parent',
      });
    }
  }, [record]);

  const save = async () => {
    setSaving(true);
    try {
      const base = record || {
        id: generateId(),
        name: currentUser.name,
        email: currentUser.email,
        rsvp: 'confirmed',
        role: 'Engineering',
        tshirtSize: 'M',
        roomNeeded: false,
      };
      await put('attendees', { ...base, ...form });
      setEditing(false);
      toast.success('Profile saved');
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      try { localStorage.setItem(`no-profile-done:${currentUser.email}`, '1'); } catch { /* ignore */ }
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const avatarColor = getAvatarColor(currentUser.name);
  const initials = getInitials(currentUser.name);

  if (loading) {
    return <PageLoader label="Loading your profile…" sublabel="Fetching your personal details" />;
  }

  const rsvpStatus = record?.rsvp || 'pending';
  const rsvpColors = { confirmed: '#166534', pending: '#854d0e', declined: '#991b1b' };
  const rsvpBg = { confirmed: '#f0fdf4', pending: '#fffbeb', declined: '#fef2f2' };
  const rsvpBorder = { confirmed: '#bbf7d0', pending: '#fef3c7', declined: '#fecaca' };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">My Profile</div>
          <div className="page-subtitle">Your personal details and emergency contact information</div>
        </div>
        {!editing && (
          <button className="btn btn-secondary" onClick={() => setEditing(true)}>
            <Edit2 size={15} /> Edit Profile
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Left column — avatar + identity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, fontWeight: 800, letterSpacing: '-1px', flexShrink: 0, boxShadow: `0 8px 24px ${avatarColor}40` }}>
              {initials}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#09090b', marginBottom: 4 }}>{currentUser.name}</div>
              <div style={{ fontSize: 13, color: '#71717a', marginBottom: 10 }}>{currentUser.email}</div>
              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: currentUser.role === 'admin' ? '#fff7ed' : '#f0f9ff', color: currentUser.role === 'admin' ? '#c2410c' : '#075985', border: `1px solid ${currentUser.role === 'admin' ? '#fed7aa' : '#bae6fd'}` }}>
                {currentUser.role}
              </span>
            </div>
          </div>

          {/* Event status */}
          <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={SECTION_LABEL}>Event Status</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={14} style={{ color: '#71717a' }} />
                  <span style={{ fontSize: 13, color: '#52525b' }}>RSVP</span>
                </div>
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: rsvpBg[rsvpStatus] || '#f4f4f5', color: rsvpColors[rsvpStatus] || '#52525b', border: `1px solid ${rsvpBorder[rsvpStatus] || '#e4e4e7'}`, textTransform: 'uppercase' }}>
                  {rsvpStatus}
                </span>
              </div>
              {record?.travelMode && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Plane size={14} style={{ color: '#71717a' }} />
                    <span style={{ fontSize: 13, color: '#52525b' }}>Travel</span>
                  </div>
                  <span style={{ fontSize: 13, color: '#09090b', textTransform: 'capitalize' }}>{record.travelMode}</span>
                </div>
              )}
              {record?.travelStatus && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={14} style={{ color: '#71717a' }} />
                    <span style={{ fontSize: 13, color: '#52525b' }}>Status</span>
                  </div>
                  <span style={{ fontSize: 13, color: '#09090b', textTransform: 'capitalize' }}>{record.travelStatus}</span>
                </div>
              )}
              {record?.tshirtSize && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <User size={14} style={{ color: '#71717a' }} />
                    <span style={{ fontSize: 13, color: '#52525b' }}>T-Shirt</span>
                  </div>
                  <span style={{ fontSize: 13, color: '#09090b' }}>{record.tshirtSize}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column — details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {editing ? (
            <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#09090b', marginBottom: 20 }}>Edit Profile</div>

              <div style={SECTION_LABEL}>Personal Info</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div>
                  <label style={LABEL_STYLE}>Phone Number</label>
                  <input style={FIELD_STYLE} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98xxx xxxxx" />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Origin City / Hometown</label>
                  <input style={FIELD_STYLE} value={form.originCity} onChange={e => setForm(f => ({ ...f, originCity: e.target.value }))} placeholder="e.g. Bangalore" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={LABEL_STYLE}>Current Address</label>
                  <input style={FIELD_STYLE} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="House/Flat, Street, City" />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Birthday</label>
                  <input type="date" style={FIELD_STYLE} value={form.birthday} onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))} />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Dietary Preference</label>
                  <select style={FIELD_STYLE} value={form.dietary} onChange={e => setForm(f => ({ ...f, dietary: e.target.value }))}>
                    {['Not specified','Vegetarian','Non-Vegetarian','Vegan','Jain'].map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LABEL_STYLE}>Blood Group</label>
                  <select style={FIELD_STYLE} value={form.bloodGroup} onChange={e => setForm(f => ({ ...f, bloodGroup: e.target.value }))}>
                    {['Unknown','A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LABEL_STYLE}>T-Shirt Size</label>
                  <select style={FIELD_STYLE} value={form.tshirtSize || 'M'} onChange={e => setForm(f => ({ ...f, tshirtSize: e.target.value }))}>
                    {['XS','S','M','L','XL','XXL'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...LABEL_STYLE, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!form.roomNeeded}
                      onChange={e => setForm(f => ({ ...f, roomNeeded: e.target.checked }))}
                      style={{ width: 16, height: 16 }}
                    />
                    Need Stay Accommodation at Venue
                  </label>
                </div>
              </div>

              <div style={SECTION_LABEL}>Emergency Contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                <div>
                  <label style={LABEL_STYLE}>Contact Name</label>
                  <input style={FIELD_STYLE} value={form.emergencyContactName} onChange={e => setForm(f => ({ ...f, emergencyContactName: e.target.value }))} placeholder="Full name" />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Contact Phone</label>
                  <input style={FIELD_STYLE} value={form.emergencyContactPhone} onChange={e => setForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} placeholder="+91 98xxx xxxxx" />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Relationship</label>
                  <select style={FIELD_STYLE} value={form.emergencyContactRelation} onChange={e => setForm(f => ({ ...f, emergencyContactRelation: e.target.value }))}>
                    {['Parent','Spouse','Sibling','Friend','Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving} style={{ opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {/* Personal details card */}
              <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={SECTION_LABEL}>Personal Details</div>
                <InfoRow icon={Phone} label="Phone" value={record?.phone} />
                <InfoRow icon={MapPin} label="Address" value={record?.address} />
                <InfoRow icon={MapPin} label="Origin City / Hometown" value={record?.originCity} />
                <InfoRow icon={Cake} label="Birthday" value={record?.birthday} />
                <InfoRow icon={Heart} label="Dietary Preference" value={record?.dietary && record.dietary !== 'Not specified' ? record.dietary : null} />
                <InfoRow icon={Droplets} label="Blood Group" value={record?.bloodGroup && record.bloodGroup !== 'Unknown' ? record.bloodGroup : null} />
                <InfoRow icon={User} label="T-Shirt Size" value={record?.tshirtSize || null} />
                {record?.roomNeeded !== undefined && (
                  <InfoRow icon={Shield} label="Stay Accommodation" value={record.roomNeeded ? 'Required' : 'Not required'} />
                )}
              </div>

              {/* Emergency contact card */}
              <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <Shield size={14} style={{ color: '#71717a' }} />
                  <div style={SECTION_LABEL}>Emergency Contact</div>
                </div>
                {record?.emergencyContactName ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#09090b' }}>{record.emergencyContactName}</div>
                    <div style={{ fontSize: 13, color: '#71717a' }}>{record.emergencyContactRelation || 'Contact'}</div>
                    {record.emergencyContactPhone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <Phone size={13} style={{ color: '#71717a' }} />
                        <span style={{ fontSize: 14, color: '#09090b' }}>{record.emergencyContactPhone}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#d4d4d8', fontStyle: 'italic' }}>No emergency contact on file — please edit your profile to add one.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
